/**
 * The lead-computing optical sight: where to put the pipper so the rounds arrive.
 *
 * ## What a gun sight is actually solving
 *
 * A round takes time to get there — nearly a second at 700 m — and three things happen during it.
 * The round **drops**, because gravity. The round **drifts**, because it left a gun that was
 * already moving sideways relative to where it was pointing. And the **target moves**, because
 * it is an aeroplane. Aim straight at a manoeuvring target and you will miss behind it, low,
 * every single time.
 *
 * So the pipper is not the boresight. It is drawn wherever the rounds are going to *be*, and the
 * pilot's job becomes trivially stateable: put the pipper on the target and pull the trigger.
 *
 * ## The gyro, and why the sight uses the aeroplane's own rotation
 *
 * The elegant part of a real LCOS, and the reason they worked decades before anyone had a
 * computer: it never measures the target's velocity. It measures the **aircraft's angular rate**,
 * and leans on one assumption — that the pilot is *tracking* the target, keeping the nose on it.
 * If that is true then the line of sight to the target is rotating at exactly the rate the
 * aeroplane is rotating, and the required lead is that rate times the time of flight. A gyro can
 * measure that; no radar required.
 *
 * The consequence is worth knowing as a player, because it is a real property of real sights:
 * **the pipper is only correct while you are smoothly tracking.** Snap the nose onto a target and
 * fire immediately and the sight is lying to you, because the rate it just measured was the
 * snap and not the target's motion. Real pilots settle into the track for a second before firing,
 * and in this simulator that is rewarded for exactly the right reason.
 *
 * ## Range
 *
 * The lead scales with time of flight, which scales with range, so a sight needs a range. Radar
 * gunsights get it from the radar; this one takes whatever the caller has — the seeker's locked
 * range when there is a lock, and a fixed default when there is not. A wrong range makes the
 * pipper wrong, which is why an unlocked gun shot in this simulator is genuinely harder.
 *
 * `updateFrame(alpha)`: yes — {@link solveLcos} is called once a frame from the HUD. It allocates
 * nothing; the integration below runs on module-level scratch.
 */

import { Vector3 } from 'three';
import { ballisticTimeOfFlight, integrateBallistic } from './ballistics.js';

/** The default range a gun sight assumes with nothing to range against [m]. */
export const DEFAULT_GUN_RANGE_M = 600;

/** How many steps the virtual round is integrated in. See {@link solveLcos}. */
const PREDICTION_STEPS = 12;

export interface LcosInput {
  /** Range to the target [m] — from the lock, or {@link DEFAULT_GUN_RANGE_M}. */
  rangeM: number;
  /** Muzzle velocity [m/s]. */
  muzzleVelocityMps: number;
  /** Inverse ballistic coefficient of the round [m²/kg]. */
  inverseBc: number;
  /** Air density [kg/m³]. */
  densityKgPerM3: number;
  /** Unit vector down the bore, sim-frame NED. */
  readonly boreNed: Vector3;
  /** The aircraft's velocity, which the rounds inherit [m/s]. */
  readonly carrierVelocityNed: Vector3;
  /** Body pitch rate [rad/s], positive nose-up. */
  pitchRateRadPerS: number;
  /** Body yaw rate [rad/s], positive nose-right. */
  yawRateRadPerS: number;
  /** Body right vector, sim-frame NED — the sight resolves its answer against this. */
  readonly boreRightNed: Vector3;
  /** Body down vector, sim-frame NED. */
  readonly boreDownNed: Vector3;
}

export function makeLcosInput(): LcosInput {
  return {
    rangeM: DEFAULT_GUN_RANGE_M,
    muzzleVelocityMps: 1_036,
    inverseBc: 4.5e-4,
    densityKgPerM3: 1.225,
    boreNed: new Vector3(1, 0, 0),
    carrierVelocityNed: new Vector3(),
    pitchRateRadPerS: 0,
    yawRateRadPerS: 0,
    boreRightNed: new Vector3(0, 1, 0),
    boreDownNed: new Vector3(0, 0, 1),
  };
}

/** Where the pipper goes, as angles off the boresight in the aircraft's own frame. */
export interface LcosSolution {
  /** Right of the boresight is positive [rad]. */
  azimuthRad: number;
  /** Below the boresight is positive [rad]. */
  elevationRad: number;
  /** Time of flight the solution was computed for [s]. */
  timeOfFlightS: number;
  /** The range used [m]. */
  rangeM: number;
}

export function makeLcosSolution(): LcosSolution {
  return { azimuthRad: 0, elevationRad: 0, timeOfFlightS: 0, rangeM: 0 };
}

const virtualPosition = /* #__PURE__ */ new Vector3();
const virtualVelocity = /* #__PURE__ */ new Vector3();

/**
 * Solve the sight.
 *
 * The ballistic half is done by *firing a virtual round* and integrating it, rather than by a
 * closed-form drop formula. That is not laziness in the other direction — a closed form would
 * have to assume the drop is `½gt²`, and `ballistics.test.ts` demonstrates that it is not, because
 * drag damps the fall as it accumulates. Twelve steps of the real integrator, once a frame, is
 * both cheaper than being wrong and exactly consistent with what the rounds themselves will do.
 */
export function solveLcos(input: LcosInput, out: LcosSolution): LcosSolution {
  const range = Math.max(1, input.rangeM);
  const timeOfFlight = ballisticTimeOfFlight(
    range,
    input.muzzleVelocityMps,
    input.inverseBc,
    input.densityKgPerM3,
  );

  // --- where the rounds go ------------------------------------------------------------------
  virtualPosition.set(0, 0, 0);
  virtualVelocity.set(
    input.carrierVelocityNed.x + input.boreNed.x * input.muzzleVelocityMps,
    input.carrierVelocityNed.y + input.boreNed.y * input.muzzleVelocityMps,
    input.carrierVelocityNed.z + input.boreNed.z * input.muzzleVelocityMps,
  );
  const step = timeOfFlight / PREDICTION_STEPS;
  for (let i = 0; i < PREDICTION_STEPS; i++) {
    integrateBallistic(virtualPosition, virtualVelocity, input.inverseBc, input.densityKgPerM3, step);
  }

  /*
   * Resolve the round's displacement into the aircraft's own axes.
   *
   * Note what is *not* subtracted: the aircraft's own travel during the time of flight. The
   * pipper answers "in what direction, from where I am now, will these rounds be", and the
   * rounds leave now. Subtracting the carrier's motion would be solving a different problem —
   * where the rounds go relative to the aeroplane — which is the tracer stream, not the sight.
   */
  const forward = virtualPosition.dot(input.boreNed);
  const right = virtualPosition.dot(input.boreRightNed);
  const down = virtualPosition.dot(input.boreDownNed);
  const safeForward = Math.max(1, forward);

  /*
   * The gyro lead.
   *
   * Assuming the pilot is tracking, the line of sight rotates with the aeroplane, so the target
   * will have moved `rate × time of flight` by the time the rounds arrive. The pipper therefore
   * *lags* the boresight, opposite the turn: pull up and it drops, roll into a right turn and
   * pull, and it swings left and down. Putting it back on the target is the pilot pulling lead.
   */
  out.azimuthRad = Math.atan2(right, safeForward) - input.yawRateRadPerS * timeOfFlight;
  out.elevationRad = Math.atan2(down, safeForward) + input.pitchRateRadPerS * timeOfFlight;
  out.timeOfFlightS = timeOfFlight;
  out.rangeM = range;
  return out;
}
